import React from 'react'
import {GetServerSideProps} from 'next'
import dynamic from 'next/dynamic'
import pathToRegexp from 'path-to-regexp'

import {
  getBrandCodeByHost,
  getBrandCodeFromUrlOrCookie,
  getBrandOverrideFromUrl
} from '@daedalus/core/src/_web/brand/utils'
import {COGNITO_LOGIN_CALLBACK_ROUTE} from '@daedalus/core/src/auth/constants'
import {COGNITO_LOGOUT_CALLBACK_ROUTE} from '@daedalus/core/src/auth/constants'
/* useBrand cannot be used here because:
 * 1. This is a Next.js page with getServerSideProps which runs on the server
 * 2. React hooks cannot be used in server-side functions
 * 3. We need brand information during SSR before React hydration
 */
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {defaultBrand, getBrandByCode} from '@daedalus/core/src/brand/config'
import {Brand} from '@daedalus/core/src/brand/types'

import {
  ACCOMMODATION_ROUTE,
  ACCOUNT_ROUTE,
  ALERTS_ROUTE,
  HOME_ROUTE,
  MEMBERSHIP_ROUTE,
  PROFILE_ROUTE,
  PROVIDER_REDIRECT_ROUTE,
  REFERRAL_ROUTE,
  SEARCH_ROUTE
} from '../constants'

export interface PageProps {
  brand: Brand
}

const AppRoutes = dynamic(
  () =>
    import(
      /* webpackMode: "eager" */
      '../AppRoutes'
    ),
  {
    ssr: false
  }
)

const Page = (props: PageProps) => {
  let {brand} = props

  if (!brand) {
    if (typeof window !== 'undefined') {
      brand = getBrandByCode(
        getBrandCodeFromUrlOrCookie(process.env.NEXT_PUBLIC_ENV)
      )
    }
    brand = defaultBrand
  }

  return <AppRoutes />
}

export default Page

const KNOWN_PATHS = [
  HOME_ROUTE,
  PROFILE_ROUTE,
  SEARCH_ROUTE,
  ACCOMMODATION_ROUTE,
  PROVIDER_REDIRECT_ROUTE,
  COGNITO_LOGIN_CALLBACK_ROUTE,
  COGNITO_LOGOUT_CALLBACK_ROUTE,
  ALERTS_ROUTE,
  MEMBERSHIP_ROUTE,
  ACCOUNT_ROUTE,
  REFERRAL_ROUTE,
  '/*debugPanel'
].map(s => pathToRegexp(s))
/**
 * This is to avoid rendering all the 404 cases, and allow the CDN to cache
 * common paths.
 *
 * Remove from the list of known paths when splitting pages.
 */
const isKnownPath = (url: string) => {
  const pathname = url.split('?')[0]
  return KNOWN_PATHS.some(path => path.test(pathname))
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  req,
  res
}) => {
  if (!isKnownPath(req.url)) {
    return {notFound: true}
  }

  // On Staging and Production, the host header is set by a lambda function
  // and the host header is not the actual host
  const hostHeader = (req.headers['x-host'] as string) || req.headers.host
  const brandCode = getBrandCodeByHost(hostHeader)
  let brand = getBrandByCode(brandCode) || defaultBrand
  if (brand.code === 'demo' || process.env.NEXT_PUBLIC_ENV !== 'production') {
    const brandOverride =
      getBrandOverrideFromUrl(req.url) ||
      getBrandByCode(req.cookies.brandOverride)
    if (brandOverride) {
      brand = brandOverride
    }
  }

  // Use the same cache control as static pages
  res.setHeader('Cache-Control', 's-maxage=31536000')

  return {props: {brand}}
}
